import React, { useState, useEffect } from 'react';
import { Link, QrCode, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import axios from '../../utils/axios';
import { useTranslation } from 'react-i18next';

interface LiveViewProps {
  menuId: string;
  menuSlug: string;
  menuTitle: string;
}

const LiveView: React.FC<LiveViewProps> = ({ menuId, menuSlug, menuTitle }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [menuUrl, setMenuUrl] = useState('');
  const [qrSize, setQrSize] = useState(200);
  const [publicMenuUrl, setPublicMenuUrl] = useState<'default' | 'light' | 'dark'>('default');

  useEffect(() => {
    fetchMenuSettings();
  }, [menuId]);

  const fetchMenuSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menu/${menuId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const menuData = response.data.data;
        
        // Get the publicMenuUrl from database (default, light, or dark)
        const savedPublicMenuUrl = menuData.publicMenuUrl || 'default';
        setPublicMenuUrl(savedPublicMenuUrl as 'default' | 'light' | 'dark');
        
        // Construct URL based on publicMenuUrl
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const baseUrl = isLocalhost 
          ? 'http://localhost:5000' 
          : import.meta.env.VITE_API_URL?.replace('/api', '') || 'https://menudigitale.kmzerowebmarketing.com';
        
        let urlPath = '/menu/';
        
        if (savedPublicMenuUrl === 'light') {
          urlPath = '/menu-light/';
        } else if (savedPublicMenuUrl === 'dark') {
          urlPath = '/menu-dark/';
        } else {
          urlPath = '/menu/';
        }
        
        const url = `${baseUrl}${urlPath}${menuSlug}`;
        setMenuUrl(url);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch menu settings:', error);
      toast.error(t('live_view.menu_link.load_error'));
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(menuUrl);
      setCopied(true);
      toast.success(t('live_view.menu_link.copy_success'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error(t('live_view.menu_link.copy_error'));
    }
  };

  const openMenu = () => {
    window.open(menuUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7BD5B5]"></div>
        <span className="ml-3 text-gray-600">{t('live_view.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-[#0A0C0B] mb-6 flex items-center gap-2">
          <Link className="w-5 h-5" />
          {t('live_view.title')}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Menu Link and QR Code */}
          <div className="space-y-6">
            {/* Menu Link */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-[#0A0C0B] mb-3 flex items-center gap-2">
                <Link className="w-4 h-4" />
                {t('live_view.menu_link.title')}
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-700">{t('live_view.menu_link.theme')}</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-gray-800 capitalize">
                    {publicMenuUrl === 'default' ? t('live_view.menu_link.default') : 
                     publicMenuUrl === 'light' ? t('live_view.menu_link.light') : 
                     t('live_view.menu_link.dark')}
                  </span>
                </div>
                
               <div className="flex flex-col sm:flex-row gap-2">
  <input
    type="text"
    value={menuUrl}
    readOnly
    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 w-full"
  />
  <button
    onClick={copyToClipboard}
    className="px-4 py-2 bg-[#7BD5B5] text-[#0A0C0B] rounded-md hover:bg-[#6bc4a5] flex items-center gap-2 w-full sm:w-auto justify-center"
  >
    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
    {copied ? t('live_view.menu_link.copied') : t('live_view.menu_link.copy')}
  </button>
</div>

              </div>
            </div>

            {/* QR Code */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-[#0A0C0B] mb-3 flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                {t('live_view.qr_code.title')}
              </h3>
              
              <div className="flex flex-col items-center space-y-4">
                <div className="p-4 bg-white border border-gray-200 rounded-lg">
                  <QRCodeSVG
                    value={menuUrl}
                    size={qrSize}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                
                <div className="w-full max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('live_view.qr_code.size')}
                  </label>
                  <input
                    type="range"
                    min="100"
                    max="300"
                    value={qrSize}
                    onChange={(e) => setQrSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-center text-sm text-gray-600 mt-1">
                    {t('live_view.qr_code.pixels', { size: qrSize })}
                  </div>
                </div>
              
              </div>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-[#0A0C0B] mb-3">
                {t('live_view.preview.title')}
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {t('live_view.preview.current_theme')} <span className="font-medium capitalize">
                      {publicMenuUrl === 'default' ? t('live_view.menu_link.default') : 
                       publicMenuUrl === 'light' ? t('live_view.menu_link.light') : 
                       t('live_view.menu_link.dark')}
                    </span>
                  </div>
               
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[300px] flex flex-col items-center justify-center">
                  <div className="text-center">
                    <QrCode className="w-16 h-16 text-[#7BD5B5]/40 mx-auto mb-4" />
                    <h4 className="font-medium text-[#0A0C0B] mb-2">
                      {menuTitle}
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      {publicMenuUrl === 'default' 
                        ? t('live_view.preview.default_theme_desc')
                        : publicMenuUrl === 'light' 
                          ? t('live_view.preview.light_theme_desc')
                          : t('live_view.preview.dark_theme_desc')}
                    </p>
                    <button
                      onClick={openMenu}
                      className="px-4 py-2 bg-[#7BD5B5] text-[#0A0C0B] rounded-md hover:bg-[#6bc4a5] flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('live_view.preview.preview_button', { 
                        theme: publicMenuUrl === 'default' ? t('live_view.menu_link.default') : 
                                publicMenuUrl === 'light' ? t('live_view.menu_link.light') : 
                                t('live_view.menu_link.dark')
                      })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveView;